// The rule editor: one schedule rule's typed reading of the contract's
// sentence (kind, when it is owed, its first date, its cadence), with the
// sentence itself beside it. Used for award rules (the deliverable page) and
// for a SOW's team rules (the assignment drawer, gate "sow").
//
// A rule with no first date is allowed: it is what the contract says. It
// mints nothing and reads "needs a start date" until someone states one.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraDateField, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraSegmentedControl, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { useCreateRule, useDeleteRule, useLogContractEvent, useUpdateRule } from '../hooks';
import { cmDate } from '../lib/format';
import { alongsideKind, CADENCES, describeRule, EVENT_KINDS, mintReceipt, OFFSET_UNITS, parseAlongside, parseTrigger, periodNoun, ruleLacksStart, triggerActFor, triggerKind } from '../lib/rule';
import { useCmLookups } from '../lookups';
import { CmDrawer } from './CmDrawer';
/**
 * The "After" choice that anchors on another deliverable (SAD-835): its
 * submission or acceptance. Held in eventKind until a deliverable is picked.
 */ const AFTER_DELIVERABLE = '__deliverable__';
export const WEEKDAYS = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
];
export const WEEKDAY_ORDINALS = [
    {
        value: '1',
        label: 'First'
    },
    {
        value: '2',
        label: 'Second'
    },
    {
        value: '3',
        label: 'Third'
    },
    {
        value: '4',
        label: 'Fourth'
    },
    {
        value: '-1',
        label: 'Last'
    }
];
/** Cadences whose period is a month or longer: a day of that period. */ const MONTH_BASED = new Set([
    'monthly',
    'quarterly',
    'semiannually',
    'annually'
]);
/** Cadences that step by weeks: a weekday. */ const WEEK_BASED = new Set([
    'weekly',
    'biweekly'
]);
/**
 * R5b-6: the cadences an "every N" interval steps (cadence_interval), with
 * the unit it counts and the named cadence that already covers an interval
 * (pangaea's chk_schedule_rules_interval refuses those).
 */ const EVERY_N = {
    weekly: {
        unit: 'weeks',
        named: {
            2: 'Every two weeks'
        }
    },
    monthly: {
        unit: 'months',
        named: {
            3: 'Quarterly',
            6: 'Twice a year',
            12: 'Yearly'
        }
    },
    annually: {
        unit: 'years',
        named: {}
    }
};
/** The editor's reading of a stored day rule. */ function dayFromRule(r) {
    const ords = r?.dayOrdinals ?? [];
    const wd = r?.dayWeekday ?? '';
    if (!r || ords.length > 1) return {
        dayMode: 'same',
        dayN: '',
        dayWeekday: wd || 'monday'
    };
    if (wd && ords.length === 1) return {
        dayMode: 'weekday_of',
        dayN: String(ords[0]),
        dayWeekday: wd
    };
    if (wd) return {
        dayMode: 'weekday',
        dayN: '',
        dayWeekday: wd
    };
    if (ords.length === 1) {
        const business = r.dayBasis === 'business_day';
        if (ords[0] === -1) return {
            dayMode: business ? 'last_business_day' : 'last_day',
            dayN: '',
            dayWeekday: 'monday'
        };
        return {
            dayMode: business ? 'business_day' : 'day',
            dayN: String(ords[0]),
            dayWeekday: 'monday'
        };
    }
    return {
        dayMode: 'same',
        dayN: '',
        dayWeekday: 'monday'
    };
}
export function draftFromRule(r, teamRule = false) {
    const along = parseAlongside(r?.eventKind);
    return {
        ...dayFromRule(r),
        alongsideDutyId: along ?? '',
        kind: along ? 'alongside' : r?.kind || 'recurring',
        gate: teamRule ? 'sow' : r?.gate || 'optional_task',
        periodOfPerformanceId: r?.periodOfPerformanceId ?? '',
        optionalTaskId: r?.optionalTaskId ?? '',
        start: r?.dueOn ? 'date' : r?.hasOffset ? 'offset' : r ? 'none' : 'offset',
        dueOn: r?.dueOn ?? '',
        offsetN: r?.hasOffset ? String(r.offsetN) : along !== null ? '0' : '',
        offsetUnit: r?.offsetUnit || 'calendar_day',
        cadence: r?.cadence || 'monthly',
        every: String(Math.max(1, r?.cadenceInterval ?? 1)),
        eventKind: r?.eventKind ?? '',
        ruleText: r?.ruleText ?? '',
        sourceLocator: r?.sourceLocator ?? '',
        base: r
    };
}
/** The day rule's columns, from the editor's day choice. */ function dayFields(d, sameCadence) {
    const b = d.base;
    const none = {
        dayOrdinals: [],
        dayWeekday: '',
        dayBasis: ''
    };
    if (d.kind !== 'recurring') return none;
    const n = Number.parseInt(d.dayN, 10);
    const monthBased = MONTH_BASED.has(d.cadence);
    switch(d.dayMode){
        case 'day':
        case 'business_day':
            return monthBased && Number.isFinite(n) ? {
                dayOrdinals: [
                    n
                ],
                dayWeekday: '',
                dayBasis: d.dayMode === 'day' ? 'calendar_day' : 'business_day'
            } : none;
        case 'last_day':
        case 'last_business_day':
            return monthBased ? {
                dayOrdinals: [
                    -1
                ],
                dayWeekday: '',
                dayBasis: d.dayMode === 'last_day' ? 'calendar_day' : 'business_day'
            } : none;
        case 'weekday_of':
            return monthBased && Number.isFinite(n) ? {
                dayOrdinals: [
                    n
                ],
                dayWeekday: d.dayWeekday,
                dayBasis: 'calendar_day'
            } : none;
        case 'weekday':
            return WEEK_BASED.has(d.cadence) ? {
                dayOrdinals: [],
                dayWeekday: d.dayWeekday,
                dayBasis: ''
            } : none;
        default:
            // "Same day": a day rule the editor cannot show (a semimonthly pair)
            // is kept while the cadence is unchanged, never silently wiped.
            return sameCadence && b && (b.dayOrdinals.length > 1 || d.cadence === 'semimonthly') ? {
                dayOrdinals: [
                    ...b.dayOrdinals
                ],
                dayWeekday: b.dayWeekday,
                dayBasis: b.dayBasis
            } : none;
    }
}
/** The typed fields ScheduleRuleFields wants; the offset keeps its sign. */ export function fieldsFromDraft(d) {
    const alongside = d.kind === 'alongside';
    const isEvent = d.kind === 'event' || alongside;
    const useOffset = isEvent || d.start === 'offset';
    const n = Number.parseInt(d.offsetN, 10);
    const hasOffset = alongside || useOffset && Number.isFinite(n);
    const b = d.base;
    // A day rule the editor cannot show is kept only while the cadence and
    // kind are unchanged.
    const sameCadence = !!b && b.kind === d.kind && b.cadence === d.cadence && d.kind === 'recurring';
    const kind = alongside ? 'event' : d.kind;
    const gate = alongside ? 'none' : d.gate;
    return {
        kind,
        gate,
        periodOfPerformanceId: [
            'base',
            'option_period'
        ].includes(gate) ? d.periodOfPerformanceId : '',
        optionalTaskId: gate === 'optional_task' ? d.optionalTaskId : '',
        dueOn: !isEvent && d.start === 'date' ? d.dueOn : '',
        hasOffset,
        offsetN: hasOffset && Number.isFinite(n) ? n : 0,
        offsetUnit: hasOffset ? alongside ? 'calendar_day' : d.offsetUnit : '',
        cadence: d.kind === 'recurring' ? d.cadence : '',
        cadenceInterval: everyInterval(d),
        ...dayFields(d, sameCadence),
        eventKind: alongside ? d.alongsideDutyId ? alongsideKind(d.alongsideDutyId) : '' : isEvent ? d.eventKind : '',
        subjects: b ? [
            ...b.subjects
        ] : [],
        ruleText: d.ruleText.trim(),
        sourceLocator: d.sourceLocator.trim(),
        clinId: b?.clinId ?? '',
        sourceDocumentId: b?.sourceDocumentId ?? '',
        effectiveFrom: b?.effectiveFrom ?? '',
        effectiveTo: b?.effectiveTo ?? '',
        establishedByModificationId: b?.establishedByModificationId ?? ''
    };
}
/**
 * The every-N interval a draft states: the one the reviewer entered on an
 * EVERY_N cadence, 0 (pangaea's default, every one) otherwise.
 */ function everyInterval(d) {
    if (d.kind !== 'recurring' || !EVERY_N[d.cadence]) return 0;
    const n = Number(d.every.trim());
    return Number.isInteger(n) && n > 1 ? n : 0;
}
/** What blocks a save (the rest the server's CHECKs name). */ export function draftProblem(d) {
    if (d.kind === 'alongside') {
        if (!d.alongsideDutyId) return 'Pick the deliverable these dates follow.';
        if (d.offsetN.trim() !== '' && !Number.isFinite(Number.parseInt(d.offsetN, 10))) return 'Days before or after must be a whole number.';
        if (!d.ruleText.trim()) return 'Say in a sentence why these are due together.';
        return null;
    }
    const every = d.kind === 'recurring' ? EVERY_N[d.cadence] : undefined;
    if (every) {
        const n = Number(d.every.trim());
        if (!Number.isInteger(n) || n < 1) return `Say every how many ${every.unit} (1 or more).`;
        if (every.named[n]) return `Every ${n} ${every.unit} is “${every.named[n]}”: pick it under Then.`;
    }
    if (d.kind === 'recurring' && MONTH_BASED.has(d.cadence)) {
        const n = Number.parseInt(d.dayN, 10);
        if (d.dayMode === 'weekday_of' && !Number.isFinite(n)) return 'Pick which weekday of the period.';
        if ((d.dayMode === 'day' || d.dayMode === 'business_day') && !(n >= 1 && n <= 31)) return d.dayMode === 'day' ? 'Say which day of the month (1–31).' : 'Say which business day (1–23).';
        if (d.dayMode === 'business_day' && n > 23) return 'No month holds more than 23 business days.';
    }
    if ([
        'base',
        'option_period'
    ].includes(d.gate) && !d.periodOfPerformanceId) return 'Pick the period this is owed in.';
    if (d.gate === 'optional_task' && !d.optionalTaskId) return 'Pick the optional task.';
    if (d.kind === 'event' && !d.eventKind) return 'Pick the event.';
    if (d.kind === 'event' && d.eventKind === AFTER_DELIVERABLE) return 'Pick the deliverable.';
    if (d.kind === 'event' && d.offsetN.trim() === '') return 'Say how long after the event.';
    if (d.kind !== 'event' && d.start === 'date' && !d.dueOn) return 'Enter the stated date.';
    if (d.kind !== 'event' && d.start === 'offset' && d.offsetN.trim() === '') return 'Say how long after the start.';
    // pangaea keeps the verbatim rule on every rule (rule_text is required).
    if (!d.ruleText.trim()) return 'Paste the sentence the rule comes from.';
    return null;
}
function draftAsRule(d) {
    const f = fieldsFromDraft(d);
    return {
        gate: f.gate ?? '',
        kind: f.kind ?? '',
        dueOn: f.dueOn ?? '',
        hasOffset: f.hasOffset ?? false,
        offsetN: f.offsetN ?? 0,
        offsetUnit: f.offsetUnit ?? '',
        cadence: f.cadence ?? '',
        cadenceInterval: f.cadenceInterval ?? 0,
        dayOrdinals: f.dayOrdinals ?? [],
        dayWeekday: f.dayWeekday ?? '',
        dayBasis: f.dayBasis ?? '',
        eventKind: f.eventKind ?? '',
        effectiveFrom: f.effectiveFrom ?? '',
        effectiveTo: f.effectiveTo ?? ''
    };
}
/** The rule a draft states, in words ("Monthly, on the 5th business day"). */ export function describeDraft(d) {
    return describeRule(draftAsRule(d));
}
/** The controlled form. `teamRule` pins the gate to the SOW. */ export function RuleForm({ programId, value, onChange, teamRule = false, dutyId = '' }) {
    const lk = useCmLookups(programId);
    const set = (k, v)=>onChange({
            ...value,
            [k]: v
        });
    const isEvent = value.kind === 'event';
    const isAlongside = value.kind === 'alongside';
    const titleOf = (id)=>lk.dutyById.get(id)?.title;
    const reading = isAlongside && !value.alongsideDutyId ? 'Pick the deliverable these dates follow.' : describeRule(draftAsRule(value), titleOf);
    const trigger = parseTrigger(value.eventKind);
    const afterDeliverable = !!trigger || value.eventKind === AFTER_DELIVERABLE;
    // The rule's words pick the act until a person does: "approval" is
    // acceptance, anything else submission.
    const act = trigger?.act ?? triggerActFor(value.ruleText);
    const lacks = !isEvent && !isAlongside && ruleLacksStart(draftAsRule(value));
    const of = periodNoun(value.cadence);
    const monthBased = MONTH_BASED.has(value.cadence);
    const weekBased = WEEK_BASED.has(value.cadence);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        "data-testid": Testids.cmDuty.ruleEditor,
        children: [
            /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "How often",
                value: value.kind,
                options: [
                    {
                        value: 'recurring',
                        label: 'Recurring'
                    },
                    {
                        value: 'dated',
                        label: 'Once'
                    },
                    {
                        value: 'event',
                        label: 'After an event'
                    },
                    ...teamRule ? [] : [
                        {
                            value: 'alongside',
                            label: 'Alongside another'
                        }
                    ]
                ],
                onValueChange: (k)=>onChange({
                        ...value,
                        kind: k,
                        offsetN: k === 'alongside' && value.offsetN.trim() === '' ? '0' : value.offsetN
                    }),
                testId: Testids.cmDuty.ruleKind
            }),
            isAlongside ? /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Due alongside",
                        required: true,
                        hint: "Each of its dates makes one here, in the same period and team.",
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: value.alongsideDutyId,
                            onChange: (e)=>set('alongsideDutyId', e.target.value),
                            "data-testid": Testids.cmDuty.ruleAlongsideDuty,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Pick the deliverable…"
                                }),
                                lk.duties.filter((d)=>d.id !== dutyId).map((d)=>/*#__PURE__*/ _jsx("option", {
                                        value: d.id,
                                        children: d.title
                                    }, d.id))
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Days before or after",
                        hint: "0 is the same day; 3 is three days later; −2 is two days before.",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            type: "number",
                            value: value.offsetN,
                            onChange: (e)=>set('offsetN', e.target.value),
                            "data-testid": Testids.cmDuty.ruleAlongsideOffset
                        })
                    })
                ]
            }) : null,
            teamRule || isAlongside ? null : /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Owed during",
                        required: true,
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: value.gate,
                            onChange: (e)=>set('gate', e.target.value),
                            "data-testid": Testids.cmDuty.ruleGate,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "optional_task",
                                    children: "An optional task’s exercises"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "base",
                                    children: "The base period"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "option_period",
                                    children: "An option period"
                                }),
                                isEvent ? /*#__PURE__*/ _jsx("option", {
                                    value: "none",
                                    children: "Whenever the event happens"
                                }) : null
                            ]
                        })
                    }),
                    value.gate === 'optional_task' ? /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Optional task",
                        required: true,
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: value.optionalTaskId,
                            onChange: (e)=>set('optionalTaskId', e.target.value),
                            "data-testid": Testids.cmDuty.ruleGateTarget,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Pick…"
                                }),
                                lk.optionalTasks.map((t)=>/*#__PURE__*/ _jsxs("option", {
                                        value: t.id,
                                        children: [
                                            t.short,
                                            t.name && t.name !== t.short ? ` · ${t.name}` : ''
                                        ]
                                    }, t.id))
                            ]
                        })
                    }) : [
                        'base',
                        'option_period'
                    ].includes(value.gate) ? /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Period",
                        required: true,
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: value.periodOfPerformanceId,
                            onChange: (e)=>set('periodOfPerformanceId', e.target.value),
                            "data-testid": Testids.cmDuty.ruleGateTarget,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Pick…"
                                }),
                                lk.periods.map((p)=>/*#__PURE__*/ _jsx("option", {
                                        value: p.id,
                                        children: p.name
                                    }, p.id))
                            ]
                        })
                    }) : null
                ]
            }),
            isAlongside ? null : isEvent ? /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "After",
                required: true,
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    value: afterDeliverable ? AFTER_DELIVERABLE : value.eventKind,
                    onChange: (e)=>set('eventKind', e.target.value),
                    "data-testid": Testids.cmDuty.ruleEventKind,
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Pick the event…"
                        }),
                        EVENT_KINDS.map((k)=>/*#__PURE__*/ _jsx("option", {
                                value: k.value,
                                children: k.label
                            }, k.value)),
                        teamRule ? null : /*#__PURE__*/ _jsx("option", {
                            value: AFTER_DELIVERABLE,
                            children: "Another deliverable is submitted or accepted"
                        })
                    ]
                })
            }) : /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "First date",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    value: value.start,
                    onChange: (e)=>set('start', e.target.value),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "offset",
                            children: "Some time after the start"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "date",
                            children: "On a stated date"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "none",
                            children: "Not stated in the contract"
                        })
                    ]
                })
            }),
            isEvent && afterDeliverable ? /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Deliverable",
                        required: true,
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: trigger?.dutyId ?? '',
                            onChange: (e)=>set('eventKind', e.target.value ? triggerKind(act, e.target.value) : AFTER_DELIVERABLE),
                            "data-testid": Testids.cmDuty.ruleTriggerDuty,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Pick the deliverable…"
                                }),
                                lk.duties.filter((d)=>d.id !== dutyId).map((d)=>/*#__PURE__*/ _jsx("option", {
                                        value: d.id,
                                        children: d.title
                                    }, d.id))
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "When it is",
                        required: true,
                        hint: "Recording that act logs the event; taking it back reverses it.",
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: act,
                            disabled: !trigger,
                            onChange: (e)=>trigger && set('eventKind', triggerKind(e.target.value, trigger.dutyId)),
                            "data-testid": Testids.cmDuty.ruleTriggerAct,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "submitted",
                                    children: "Submitted"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "accepted",
                                    children: "Accepted (approved at its final tier)"
                                })
                            ]
                        })
                    })
                ]
            }) : null,
            !isEvent && !isAlongside && value.start === 'date' ? /*#__PURE__*/ _jsx(SpecraDateField, {
                picker: "native",
                label: "Stated date",
                required: true,
                value: value.dueOn,
                onValueChange: (v)=>set('dueOn', v),
                "data-testid": Testids.cmDuty.ruleDueOn
            }) : null,
            isEvent || !isAlongside && value.start === 'offset' ? /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: isEvent ? 'How long after' : 'How long after the start',
                        required: true,
                        hint: "A negative number means before, e.g. −5.",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            type: "number",
                            value: value.offsetN,
                            onChange: (e)=>set('offsetN', e.target.value),
                            "data-testid": Testids.cmDuty.ruleOffsetN
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Unit",
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            value: value.offsetUnit,
                            onChange: (e)=>set('offsetUnit', e.target.value),
                            "data-testid": Testids.cmDuty.ruleOffsetUnit,
                            children: OFFSET_UNITS.map((u)=>/*#__PURE__*/ _jsx("option", {
                                    value: u.value,
                                    children: u.label
                                }, u.value))
                        })
                    })
                ]
            }) : null,
            value.kind === 'recurring' ? /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Then",
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            value: value.cadence,
                            onChange: (e)=>set('cadence', e.target.value),
                            "data-testid": Testids.cmDuty.ruleCadence,
                            children: CADENCES.map((c)=>/*#__PURE__*/ _jsx("option", {
                                    value: c.value,
                                    children: c.label
                                }, c.value))
                        })
                    }),
                    EVERY_N[value.cadence] ? /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: `Every how many ${EVERY_N[value.cadence].unit}`,
                        hint: `1 is every one; 3 is “every 3 ${EVERY_N[value.cadence].unit}”.`,
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            type: "number",
                            min: 1,
                            value: value.every,
                            onChange: (e)=>set('every', e.target.value),
                            "data-testid": Testids.cmDuty.ruleEvery
                        })
                    }) : null,
                    monthBased || weekBased ? /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "On",
                        hint: monthBased ? `Business days skip weekends and federal holidays. A date on a weekend or holiday moves as the contract's roll says.` : undefined,
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: value.dayMode,
                            onChange: (e)=>set('dayMode', e.target.value),
                            "data-testid": Testids.cmDuty.ruleDayMode,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "same",
                                    children: "The same day as the first date"
                                }),
                                monthBased ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("option", {
                                            value: "day",
                                            children: [
                                                "A day of the ",
                                                of,
                                                " (e.g. the 15th)"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("option", {
                                            value: "business_day",
                                            children: [
                                                "A business day of the ",
                                                of,
                                                " (e.g. the 5th)"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("option", {
                                            value: "last_day",
                                            children: [
                                                "The last day of the ",
                                                of
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("option", {
                                            value: "last_business_day",
                                            children: [
                                                "The last business day of the ",
                                                of
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("option", {
                                            value: "weekday_of",
                                            children: [
                                                "A weekday of the ",
                                                of,
                                                " (e.g. the last Thursday)"
                                            ]
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsx("option", {
                                    value: "weekday",
                                    children: "A weekday (e.g. every Friday)"
                                })
                            ]
                        })
                    }) : null
                ]
            }) : null,
            value.kind === 'recurring' && monthBased && (value.dayMode === 'day' || value.dayMode === 'business_day') ? /*#__PURE__*/ _jsx(SpecraFormField, {
                label: value.dayMode === 'day' ? `Day of the ${of}` : `Business day of the ${of}`,
                required: true,
                hint: value.dayMode === 'day' ? 'A month without that day is skipped and flagged.' : undefined,
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    type: "number",
                    min: 1,
                    max: value.dayMode === 'day' ? 31 : 23,
                    value: value.dayN,
                    onChange: (e)=>set('dayN', e.target.value),
                    "data-testid": Testids.cmDuty.ruleDayN
                })
            }) : null,
            value.kind === 'recurring' && (monthBased && value.dayMode === 'weekday_of' || weekBased && value.dayMode === 'weekday') ? /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                children: [
                    monthBased ? /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Which one",
                        required: true,
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: value.dayN,
                            onChange: (e)=>set('dayN', e.target.value),
                            "data-testid": Testids.cmDuty.ruleDayN,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Pick…"
                                }),
                                WEEKDAY_ORDINALS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                        value: o.value,
                                        children: o.label
                                    }, o.value))
                            ]
                        })
                    }) : null,
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Weekday",
                        required: true,
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            value: value.dayWeekday,
                            onChange: (e)=>set('dayWeekday', e.target.value),
                            "data-testid": Testids.cmDuty.ruleDayWeekday,
                            children: WEEKDAYS.map((w)=>/*#__PURE__*/ _jsx("option", {
                                    value: w,
                                    children: w[0].toUpperCase() + w.slice(1)
                                }, w))
                        })
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "As the contract states it",
                required: true,
                hint: "Paste the sentence, e.g. “Due seven days after Optional Task Exercise and monthly thereafter.”",
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    rows: 2,
                    value: value.ruleText,
                    onChange: (e)=>set('ruleText', e.target.value),
                    "data-testid": Testids.cmDuty.ruleTextInput
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Where it says so",
                hint: "e.g. “Award PWS 5.4.7.1, p.66”",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    value: value.sourceLocator,
                    onChange: (e)=>set('sourceLocator', e.target.value)
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                "data-testid": Testids.cmDuty.ruleReading,
                children: [
                    "→ ",
                    reading
                ]
            }),
            lacks ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "warning",
                variant: "framed",
                "data-testid": Testids.cmDuty.needsStart,
                children: "Needs a start date: this rule has no first date, so no dates exist until one is stated."
            }) : null
        ]
    });
}
/** The award-rule drawer on the deliverable page: create, edit or delete. */ export function RuleEditorDrawer({ programId, dutyId, rule, onClose, onSaved }) {
    const [draft, setDraft] = useState(()=>draftFromRule(rule));
    const create = useCreateRule(programId, {
        errors: 'inline'
    });
    const update = useUpdateRule(programId, {
        errors: 'inline'
    });
    const del = useDeleteRule(programId, {
        errors: 'inline'
    });
    const busy = create.isPending || update.isPending || del.isPending;
    const problem = draftProblem(draft);
    const error = create.error ?? update.error ?? del.error;
    const save = ()=>{
        const fields = fieldsFromDraft(draft);
        const done = {
            onSuccess: (r)=>onSaved(mintReceipt(r.result?.mint))
        };
        if (rule) update.mutate({
            ruleId: rule.id,
            fields
        }, done);
        else create.mutate({
            dutyId,
            fields
        }, done);
    };
    return /*#__PURE__*/ _jsxs(CmDrawer, {
        title: rule ? 'Edit rule' : 'Add the rule the award states',
        description: "Each date comes from a rule. Saving re-schedules this deliverable's dates.",
        onClose: onClose,
        busy: busy,
        testId: Testids.cmDuty.ruleEditorDrawer,
        footerProps: {
            confirmLabel: 'Save rule',
            onConfirm: save,
            confirmDisabled: !!problem || busy,
            loading: busy,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmDuty.ruleSave
            },
            leading: rule ? /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "destructive",
                disabled: busy,
                onClick: ()=>del.mutate({
                        ruleId: rule.id
                    }, {
                        onSuccess: (r)=>onSaved(mintReceipt(r.result?.mint))
                    }),
                "data-testid": Testids.cmDuty.ruleDelete,
                children: "Delete rule"
            }) : problem ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: problem
            }) : undefined
        },
        children: [
            /*#__PURE__*/ _jsx(RuleForm, {
                programId: programId,
                value: draft,
                onChange: setDraft,
                dutyId: dutyId
            }),
            error ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "caption",
                tone: "danger",
                role: "alert",
                className: "mt-3",
                children: apiErrorMessage(error, 'Could not save the rule')
            }) : null
        ]
    });
}
/**
 * D107: an option period's "N days ARO" rule has no order date until one is
 * logged. When one applied modification exercised the period, its date is
 * offered — never applied until the reviewer clicks (D98).
 */ export function AroSuggestion({ programId, check, canWrite }) {
    const log = useLogContractEvent(programId, {
        success: (r)=>`Order date logged: ${cmDate(r.event?.occurredOn)}. ${mintReceipt(r.result?.mint)}`
    });
    if (!check.suggestedOn) return null;
    const mod = check.suggestedLabel || 'the modification';
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-2",
        "data-testid": Testids.cmDuty.aroSuggestion,
        children: [
            /*#__PURE__*/ _jsxs(SpecraText, {
                size: "caption",
                tone: "muted",
                children: [
                    mod,
                    " exercised this period on ",
                    cmDate(check.suggestedOn),
                    "."
                ]
            }),
            canWrite ? /*#__PURE__*/ _jsxs(SpecraButton, {
                size: "sm",
                variant: "secondary",
                isLoading: log.isPending,
                onClick: ()=>log.mutate({
                        kind: 'receipt_of_order',
                        occurredOn: check.suggestedOn,
                        label: `Reviewer accepted ${mod}'s date`,
                        sourceDocumentId: check.suggestedDocumentId,
                        // The period's order date, for the team whose date waits on it.
                        ...check.workstreamId ? {
                            workstreamId: check.workstreamId
                        } : {}
                    }),
                "data-testid": Testids.cmDuty.aroAccept,
                children: [
                    "Use ",
                    mod,
                    "’s date (",
                    cmDate(check.suggestedOn),
                    ") as the order date"
                ]
            }) : null
        ]
    });
}
